import type { UsermanagementApi } from '@liks/lac-svc-api/esm/apis/UsermanagementApi';
import type { User } from '@liks/lac-svc-api/esm/models/index';
import { ResponseError } from '@liks/lac-svc-api/esm/runtime';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isUserNotFoundError(error: unknown): boolean {
  if (error instanceof ResponseError && error.response.status === 404) {
    return true;
  }
  if (error instanceof Error) {
    return /user not found/i.test(error.message);
  }
  return false;
}

/**
 * Keycloak can lag briefly after create; wait until the user can be read
 * before follow-up calls like setting roles.
 */
export async function waitUntilUserReadable(
  usermanagementApi: UsermanagementApi,
  tenantName: string,
  userRef: string,
  options?: { timeoutMs?: number; intervalMs?: number },
): Promise<User> {
  const timeoutMs = options?.timeoutMs ?? 10_000;
  const intervalMs = options?.intervalMs ?? 300;
  const deadline = Date.now() + timeoutMs;
  let lastError: unknown;

  while (Date.now() < deadline) {
    try {
      return await usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceReadUser(
        {
          tenantName,
          userRef,
        },
      );
    } catch (error) {
      lastError = error;
      if (!isUserNotFoundError(error)) {
        throw error;
      }
      await sleep(intervalMs);
    }
  }

  if (lastError instanceof Error) {
    throw lastError;
  }
  throw new Error(`User "${userRef}" was not readable after create`);
}

export async function setUserRolesWithRetry(
  setUserRoles: (
    tenantName: string,
    userRef: string,
    roleNames: string[],
  ) => Promise<void>,
  tenantName: string,
  userRef: string,
  roleNames: string[],
  options?: { intervalMs?: number },
): Promise<void> {
  const intervalMs = options?.intervalMs ?? 300;

  try {
    await setUserRoles(tenantName, userRef, roleNames);
    return;
  } catch (error) {
    if (!isUserNotFoundError(error)) {
      throw error;
    }
  }

  await sleep(intervalMs);
  await setUserRoles(tenantName, userRef, roleNames);
}
